import type { PropsWithChildren, ReactElement } from 'react'
import { createContext, useContext } from 'react'
import { noop } from 'lodash-es'
import ReactModal from 'react-modal'
import cc from 'classcat'

const style: ReactModal.Styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  // reset react-modal default inline styles
  content: {
    position: undefined,
    top: undefined,
    left: undefined,
    right: undefined,
    bottom: undefined,
    border: undefined,
    background: undefined,
    overflow: undefined,
    WebkitOverflowScrolling: undefined,
    borderRadius: undefined,
    outline: undefined,
    padding: undefined,
  },
}

const ModalDialogContext = createContext({ onRequestClose: noop })

export default function ModalDialog({
  children,
  className,
  contentLabel,
  isOpen,
  onRequestClose = noop,
}: Readonly<PropsWithChildren<ReactModal.Props>>): ReactElement {
  // Safe to use this app element because we are only using this component in the editor.
  const appElement = document.querySelector('div.ep-is-editor-mode') as HTMLElement

  return (
    <ModalDialogContext.Provider value={{ onRequestClose }}>
      <ReactModal
        isOpen={isOpen}
        appElement={appElement}
        contentLabel={contentLabel}
        onRequestClose={onRequestClose}
        style={style}
        className={cc(['ep-ui ep-modal-dialog', className])}
      >
        <div className="ep-modal-content">{children}</div>
      </ReactModal>
    </ModalDialogContext.Provider>
  )
}

function ModalDialogHeader({ title }: Readonly<PropsWithChildren<{ title: string }>>): ReactElement {
  const { onRequestClose } = useContext(ModalDialogContext)

  return (
    <div className="ep-modal-header">
      <span className="ep-modal-close" onClick={onRequestClose} />
      <div className="ep-modal-title">{title}</div>
    </div>
  )
}

function ModalDialogBody({ children }): ReactElement {
  return <div className="ep-modal-body">{children}</div>
}

function ModalDialogFooter({ children }): ReactElement {
  return <div className="ep-modal-footer">{children}</div>
}

ModalDialog.Header = ModalDialogHeader
ModalDialog.Body = ModalDialogBody
ModalDialog.Footer = ModalDialogFooter
